import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useDeleteStopMutation } from 'tripPlanner/hooks/api/chat'
import type { DrawerHotel } from '../components/HotelListingDrawer'
import type { DrawerExperience } from '../components/ExperienceListingDrawer/ExperienceDetailView'
import TripPlanDetailDrawer from '../components/TripPlanDetailDrawer'
import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import LocationInfoDrawer from '../components/LocationInfoDrawer'
import ChatOfferDetailDrawer from '../components/ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from '../components/ChatExperienceDetailDrawer'
import { useDrawerState } from '../hooks/useDrawerState'
import { useTripPlannerEventBus, useEventSubscription } from '../contexts/TripPlannerEventBus'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { trackHotelDrawerOpened, trackExperienceDrawerOpened } from 'tripPlanner/utils/chatAnalytics'

function DrawerContainer() {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const { sendMessage } = useChatActions()
  const { chatId } = useChatStreamStatus()
  const { itineraryData, refetchCurrentChatItinerary } = useItineraryContext()

  const {
    detailPanel,
    placeInfo,
    locationInfo,
    offerDrawer,
    resetDrawers,
  } = useDrawerState()

  const prevDrawerOpenRef = useRef(false)
  useEffect(() => {
    const justOpened = offerDrawer.isOpen && !prevDrawerOpenRef.current
    prevDrawerOpenRef.current = offerDrawer.isOpen
    if (!justOpened || !chatId) return
    if (offerDrawer.data?.offerType === 'experience') {
      trackExperienceDrawerOpened(chatId)
    } else {
      trackHotelDrawerOpened(chatId)
    }
  }, [offerDrawer.isOpen, offerDrawer.data?.offerType, chatId])

  const deleteStopMutation = useDeleteStopMutation()

  const handleDeleteStopFromDrawer = useCallback((stopId: number) => {
    deleteStopMutation.mutate(
      { stopId },
      {
        onSuccess: () => {
          showSnackbar(intl.formatMessage({
            id: 'bTOBsXcsZanMrwJG-9_W',
            defaultMessage: 'Stop deleted successfully',
            description: 'Success snackbar message after deleting a stop from the trip itinerary',
          }), 'success')
          refetchCurrentChatItinerary()
        },
        onError: () => {
          showSnackbar(intl.formatMessage({
            id: 'eiuITzmc4eW9taNKmjvj',
            defaultMessage: 'Failed to delete stop. Please try again.',
            description: 'Error snackbar message when stop deletion fails in the trip itinerary',
          }), 'critical')
        },
      },
    )
  }, [deleteStopMutation, refetchCurrentChatItinerary, intl])

  const handleOfferShowMap = useCallback((hotel: DrawerHotel) => {
    offerDrawer.close()
    if (!hotel.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation,
        offerType: 'hotel',
      },
    })
  }, [offerDrawer, eventBus])

  const handleExperienceDrawerShowMap = useCallback((experience: DrawerExperience) => {
    offerDrawer.close()
    if (!experience.geoLocation) return
    eventBus.dispatch({
      type: 'show_offer_suggestion_on_map',
      data: {
        offerId: experience.offerId,
        name: experience.name,
        coordinates: experience.geoLocation,
        offerType: 'experience',
      },
    })
  }, [offerDrawer, eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('open_place_drawer', (event) => {
    placeInfo.open({ googlePlaceId: event.googlePlaceId, placeName: event.placeName })
  })

  useEventSubscription('open_location_drawer', (event) => {
    locationInfo.open(event.data)
  })

  useEventSubscription('open_detail_drawer', (event) => {
    detailPanel.open({
      googlePlaceId: event.googlePlaceId,
      placeName: event.placeName,
      countryName: event.countryName,
      stopId: event.stopId,
    })
  })

  useEventSubscription('open_offer_drawer', (event) => {
    offerDrawer.open({ offerId: event.offerId, searchParams: event.searchParams, offerType: event.offerType })
  })

  useEventSubscription('reset_chat_state', () => {
    resetDrawers()
  })

  return (
    <>
      {detailPanel.isOpen && detailPanel.data && (
        <TripPlanDetailDrawer
          isOpen={detailPanel.isOpen}
          onDismiss={detailPanel.close}
          onAfterClose={detailPanel.afterClose}
          googlePlaceId={detailPanel.data.googlePlaceId}
          placeName={detailPanel.data.placeName}
          countryName={detailPanel.data.countryName}
          stopId={detailPanel.data.stopId}
          onDeleteStop={detailPanel.data.stopId !== undefined ? handleDeleteStopFromDrawer : undefined}
        />
      )}
      {placeInfo.isOpen && placeInfo.data && (
        <PlaceInfoDrawer
          isOpen={placeInfo.isOpen}
          onClose={placeInfo.close}
          onAfterClose={placeInfo.afterClose}
          googlePlaceId={placeInfo.data.googlePlaceId}
          placeName={placeInfo.data.placeName}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {locationInfo.isOpen && locationInfo.data && (
        <LocationInfoDrawer
          isOpen={locationInfo.isOpen}
          onClose={locationInfo.close}
          onAfterClose={locationInfo.afterClose}
          locationData={locationInfo.data}
          category={locationInfo.data.category}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {offerDrawer.data?.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          offerId={offerDrawer.data.offerId}
          isOpen={offerDrawer.isOpen}
          onClose={offerDrawer.close}
          onAfterClose={offerDrawer.afterClose}
          onShowMap={handleExperienceDrawerShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
        />
      )}
      {offerDrawer.data && offerDrawer.data.offerType !== 'experience' && (
        <ChatOfferDetailDrawer
          offerId={offerDrawer.data.offerId}
          isOpen={offerDrawer.isOpen}
          onClose={offerDrawer.close}
          onAfterClose={offerDrawer.afterClose}
          onShowMap={handleOfferShowMap}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          searchParams={offerDrawer.data.searchParams}
        />
      )}
    </>
  )
}

export default DrawerContainer
